/**
 * Formats a timestamp as a relative time string showing the elapsed time from now.
 * Returns the time difference in the largest applicable unit (years, months, days, hours, or minutes).
 *
 * @param timestamp - The Date object to compare against the current time
 * @returns A string representation of the time elapsed in format "Xy", "XM", "Xd", "Xh", or "Xm"
 *          where X is the number of years, months, days, hours, or minutes respectively
 *
 * @example
 * ```typescript
 * const now = new Date();
 * const fiveMinutesAgo = new Date(now.getTime() - 5 * 60 * 1000);
 * const twoHoursAgo = new Date(now.getTime() - 2 * 60 * 60 * 1000);
 * const threeDaysAgo = new Date(now.getTime() - 3 * 24 * 60 * 60 * 1000);
 * const twoMonthsAgo = new Date(now.getTime() - 60 * 24 * 60 * 60 * 1000);
 * const oneYearAgo = new Date(now.getTime() - 365 * 24 * 60 * 60 * 1000);
 *
 * formatTime(fiveMinutesAgo)  // "5M"
 * formatTime(twoHoursAgo)     // "2H"
 * formatTime(threeDaysAgo)    // "3d"
 * formatTime(threeWeeksAgo)   // "3w"
 * formatTime(twoMonthsAgo)    // "2m"
 * formatTime(oneYearAgo)      // "1y"
 * formatTime(now)             // "0m"
 * ```
 *
 * @remarks
 * - If the timestamp is in the future, it will return "0m"
 * - Time is always rounded down to the nearest whole unit
 * - Years take precedence over months, months over days, days over hours, hours over minutes
 * - Assumes 30 days per month and 365 days per year for simplified calculations
 */
export const formatTime = (timestamp: Date): string => {
	if (typeof timestamp !== "object") return "...";
	const now = new Date();
	const diffInMs = now.getTime() - timestamp.getTime();
	const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
	const diffInHours = Math.floor(diffInMinutes / 60);
	const diffInDays = Math.floor(diffInHours / 24);
	const diffInWeeks = Math.floor(diffInDays / 7);
	const diffInMonths = Math.floor(diffInDays / 30); // Assuming 30 days per month
	const diffInYears = Math.floor(diffInDays / 365); // Assuming 365 days per year

	if (diffInYears > 0) {
		return `${diffInYears}y`;
	} else if (diffInMonths > 0) {
		return `${diffInMonths}m`;
	} else if (diffInWeeks > 0) {
		return `${diffInWeeks}w`;
	} else if (diffInDays > 0) {
		return `${diffInDays}d`;
	} else if (diffInHours > 0) {
		return `${diffInHours}H`;
	} else {
		return `${diffInMinutes}M`;
	}
};
